import {
  createModule,
  action,
  mutation,
} from 'vuex-class-component';
import { RepositoryFactory } from '@/repository/factory/repositoryFactory';
import {
  FindOneProjectResponse,
  FindProjectTaskAttributeValueOptionsResponse,
  FindOneProjectQuery,
  CreateProjectDto,
  LoadProjectBasic,
  UpdateProjectParams,
  Project,
  UpdateCustomAttributesDto,
  CreateProjectTaskAttributeValueOption,
  UpdateProjectCustomAttributesParam,
  UpdateProjectTaskAttributeValueOptionsParam,
  UpdateTaskAttributeValueOptionsQuery,
  TaskAttributeValueOption,
  LoadFindProjects
} from '@/@types/Project';
import { UpdateModeQueryDto } from '@/@types/Common';

const VuexModule = createModule({
  namespaced: 'theoryApiProjectsStore',
  strict: false,
});

export class ProjectsStore extends VuexModule {
  private readonly projectsRepository = RepositoryFactory.theoryApi.projectsRepository();
  /** state */
  private project: FindOneProjectResponse | null = null;
  private projectBasic: LoadProjectBasic | null = null;
  private projectAll: Project | null = null;
  private taskAttributeValueOptions: FindProjectTaskAttributeValueOptionsResponse | null = null;
  private createdTaskAttributeValueOption: TaskAttributeValueOption | null = null;
  private allProjects: LoadFindProjects[] = [];

  @mutation
  private setProject(payload: FindOneProjectResponse | null) {
    this.project = payload;
  }

  @mutation
  private setProjectBasic(payload: LoadProjectBasic | null) {
    this.projectBasic = payload;
  }

  @mutation
  private setProjectAll(payload: Project | null) {
    this.projectAll = payload;
  }

  @mutation
  private setTaskAttributeValueOptions(
    payload: FindProjectTaskAttributeValueOptionsResponse | null
  ) {
    this.taskAttributeValueOptions = payload;
  }

  @mutation
  private setCreatedTaskAttributeValueOption(
    payload: TaskAttributeValueOption | null,
  ) {
    this.createdTaskAttributeValueOption = payload;
  }

  @mutation
  private setAllProjects(payload: LoadFindProjects[]) {
    this.allProjects = payload;
  }

  @mutation
  private removeProjectByOrganizationIdAndProjectId(params: { organizationId: number, projectId: number; }) {
    const removedProjects = this.allProjects.filter(x => x.organizationId !== params.organizationId && x.projectId !== params.projectId);
    this.allProjects = removedProjects;
  }

  @mutation
  private addProject(payload: LoadFindProjects) {
    this.allProjects.push(payload);
  }

  /** プロジェクト登録 */
  @action
  async createProject(createProjectDto: CreateProjectDto) {
    const createResult = await this.projectsRepository.createProject(createProjectDto);
    if (createResult) {
      this.fetchAllProjects(createProjectDto.organizationId);
    }
    return createResult;
  }

  /** プロジェクト詳細取得 */
  @action
  async fetchProject(params: {
    projectId: number,
    query: FindOneProjectQuery,
  }) {
    const results = await this.projectsRepository.findOneProject(
      params.projectId,
      params.query,
    );
    this.setProject(results);
  }

  /** プロジェクト詳細取得（Basic） */
  @action
  async fetchProjectBasic(projectId: number) {
    const results = await this.projectsRepository.fetchOneProject(projectId);
    if (results) {
      this.setProjectBasic(results);
    }
  }

  /** プロジェクト詳細取得（All） */
  @action
  async fetchProjectAll(projectId: number) {
    const results = await this.projectsRepository.fetchOneProjectDetail(projectId);
    if (results) {
      this.setProjectAll(results);
    }
  }

  /** 課題属性値選択項目一覧取得 */
  @action
  async fetchTaskAttributeValueOptions(projectId: number) {
    const results = await this.projectsRepository.findProjectTaskAttributeValueOptions(
      projectId,
    );
    this.setTaskAttributeValueOptions(results);
  }

  /** プロジェクト部分更新 */
  @action
  async updateProjectPart(updateParams: UpdateProjectParams) {
    await this.projectsRepository.updateProjectPart(updateParams);
    await this.fetchProjectBasic(updateParams.projectId);
  }

  /** カスタム属性一括更新 */
  @action
  async updateCustomAttributes({ projectId, data }: UpdateCustomAttributesDto) {
    await this.projectsRepository.updateProjectCustomAttributes(projectId, data, { 'update-mode': 'all'});
    await this.fetchProjectAll(projectId);
  }

  /** 課題属性選択項目削除 */
  @action
  async deleteTaskAttributeValueOption({ projectId, taskAttributeValueOptionId }: { projectId: number; taskAttributeValueOptionId: number; }) {
    await this.projectsRepository.deleteTaskAttributeValueOption(projectId, taskAttributeValueOptionId);
    await this.fetchTaskAttributeValueOptions(projectId);
  }

  /** 課題属性選択項目登録 */
  @action
  async createTaskAttributeValueOption(data: CreateProjectTaskAttributeValueOption) {
    const response = await this.projectsRepository.createTaskAttributeValueOption(data.projectId, data);
    if (response) { this.setCreatedTaskAttributeValueOption(response.results) }
    await this.fetchTaskAttributeValueOptions(data.projectId);
  }

  /** プロジェクトカスタム属性更新 */
  @action
  async updateProjectCustomAttributes(params: {
    projectId: number,
    param: UpdateProjectCustomAttributesParam,
    query: UpdateModeQueryDto,
  }) {
    await this.projectsRepository.updateProjectCustomAttributes(
      params.projectId,
      params.param,
      params.query,
    );
  }

  /** 課題属性値選択項目更新 */
  @action
  async updateProjectTaskAttributeValueOptions(params: {
    projectId: number,
    param: UpdateProjectTaskAttributeValueOptionsParam,
    query: UpdateTaskAttributeValueOptionsQuery,
  }) {
    await this.projectsRepository.updateProjectTaskAttributeValueOptions(
      params.projectId,
      params.param,
      params.query,
    );
  }

  @action
  async deleteProject() {
    this.setProject(null);
  }

  @action
  async deleteProjectAll() {
    this.setProjectAll(null);
  }

  @action
  async deleteProjectBasic() {
    this.setProjectBasic(null);
  }

  @action
  async deleteTaskAttributeValueOptions() {
    this.setTaskAttributeValueOptions(null);
  }

  @action
  async fetchAllProjects(organizationId: number) {
    const results = await this.projectsRepository.getAllProjects(organizationId);
    if (results) {
      this.setAllProjects(results);
    }
  }

  @action
  async deleteProjectByOrganizationIdAndProjectId(params: { organizationId: number, projectId: number }) {
    await this.projectsRepository.deleteProject(params.organizationId, params.projectId);
    this.removeProjectByOrganizationIdAndProjectId(params);
  }

  get getProject() {
    return this.project;
  }

  get getProjectBasic() {
    return this.projectBasic;
  }

  get getProjectAll() {
    return this.projectAll;
  }

  get getTaskAttributeValueOptions() {
    return this.taskAttributeValueOptions;
  }

  get getCreatedTaskAttributeValueOption() {
    return this.createdTaskAttributeValueOption;
  }

  get getAllProjects() {
    return this.allProjects;
  }
}
